'use client';

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { Button, type ButtonProps } from './Button';
import { TextLink } from './TextLink';

export type DialogueAction = {
  label: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  buttonProps?: Omit<ButtonProps, 'children' | 'onClick' | 'variant'>;
};

export interface DialogueProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  description?: React.ReactNode;
  primaryAction?: DialogueAction;
  secondaryAction?: DialogueAction;
  /** Optional third action (e.g. "Reset") */
  restAction?: DialogueAction;
  /**
   * When true, renders the dialogue in a portal with a backdrop (like `Modal`).
   * Control visibility via `open` (or by conditional rendering) and close via `onOpenChange`.
   */
  withOverlay?: boolean;
  /** Used when `withOverlay` is enabled. */
  open?: boolean;
  /** Used when `withOverlay` is enabled. */
  onOpenChange?: (open: boolean) => void;
  /** Used when `withOverlay` is enabled. */
  dismissible?: boolean;
  /** Used when `withOverlay` is enabled. */
  ariaLabel?: string;
  /** Used when `withOverlay` is enabled. */
  overlayClassName?: string;
  /** Used when `withOverlay` is enabled. */
  backdropClassName?: string;
  /** Used when `withOverlay` is enabled. */
  containerClassName?: string;
}

export function Dialogue({
  title,
  description,
  primaryAction,
  secondaryAction,
  restAction,
  withOverlay = false,
  open = true,
  onOpenChange,
  dismissible = true,
  ariaLabel = 'Dialog',
  overlayClassName,
  backdropClassName,
  containerClassName,
  className,
  ...rest
}: DialogueProps) {
  const hasActions = Boolean(primaryAction) || Boolean(secondaryAction) || Boolean(restAction);

  useEffect(() => {
    if (!withOverlay) return;
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, withOverlay]);

  useEffect(() => {
    if (!withOverlay || !open || !dismissible) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOpenChange?.(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [dismissible, onOpenChange, open, withOverlay]);

  if (withOverlay && !open) return null;

  const card = (
    <div
      className={cn(
        'w-full',
        'max-w-[342px]',
        'bg-primary border border-tertiary shadow-lg rounded-4xl',
        'p-6',
        'flex flex-col gap-6',
        restAction ? 'md:max-w-[560px]' : 'md:max-w-[480px]',
        className,
      )}
      {...rest}
    >
      <div className="flex flex-col gap-2 w-full text-primary">
        <div className="w-full">
          <h3 className="text-body-1-high md:text-heading-6">{title}</h3>
        </div>
        {description ? <div className="text-body-2 w-full">{description}</div> : null}
      </div>

      {hasActions ? (
        restAction ? (
          <div className="w-full flex flex-col-reverse gap-4 md:flex-row md:items-center md:justify-between">
            <TextLink
              className={cn(
                'w-full justify-center py-2 md:w-auto md:justify-start md:shrink-0 whitespace-nowrap',
              )}
              onClick={restAction.onClick}
              disabled={Boolean(restAction.buttonProps?.disabled)}
            >
              {restAction.label}
            </TextLink>
            <div className="w-full flex flex-col-reverse gap-4 md:w-auto md:flex-row md:items-center md:justify-end">
              {secondaryAction ? (
                <Button
                  variant="secondary"
                  className={cn('w-full md:w-auto')}
                  onClick={secondaryAction.onClick}
                  {...secondaryAction.buttonProps}
                >
                  {secondaryAction.label}
                </Button>
              ) : null}
              {primaryAction ? (
                <Button
                  variant="primary"
                  className={cn('w-full md:w-auto')}
                  onClick={primaryAction.onClick}
                  {...primaryAction.buttonProps}
                >
                  {primaryAction.label}
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <div
            className={cn(
              'w-full',
              primaryAction && secondaryAction
                ? 'flex flex-col-reverse md:flex-row gap-4'
                : 'flex flex-col gap-4',
            )}
          >
            {secondaryAction ? (
              <Button
                variant="secondary"
                className={cn('w-full', primaryAction ? 'md:flex-1 md:w-auto' : null)}
                onClick={secondaryAction.onClick}
                {...secondaryAction.buttonProps}
              >
                {secondaryAction.label}
              </Button>
            ) : null}
            {primaryAction ? (
              <Button
                variant="primary"
                className={cn('w-full', secondaryAction ? 'md:flex-1 md:w-auto' : null)}
                onClick={primaryAction.onClick}
                {...primaryAction.buttonProps}
              >
                {primaryAction.label}
              </Button>
            ) : null}
          </div>
        )
      ) : null}
    </div>
  );

  if (!withOverlay) return card;

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (dismissible && e.target === e.currentTarget) {
      onOpenChange?.(false);
    }
  };

  const overlay = (
    <div
      className={cn('fixed inset-0 z-50 flex items-center justify-center p-4', overlayClassName)}
      onClick={handleOverlayClick}
    >
      <div className={cn('fixed inset-0 bg-black/50', backdropClassName)} aria-hidden="true" />
      <div
        className={cn('relative z-10 w-full flex justify-center', containerClassName)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        onClick={e => e.stopPropagation()}
      >
        {card}
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(overlay, document.body) : null;
}

export default Dialogue;
